import {
  useQuery,
  useMutation,
  useQueryClient,
  useIsFetching,
} from '@tanstack/react-query'
import { apis } from '@/config/apiConfig'
import { apiFetch } from '@/utilities/api'

interface LayerProperty {
  key: string
  name: string
  type: 'string' | 'number' | 'date' | 'currency' | 'longtext' | 'document'
  unique?: string[] | boolean
  default?: string | number | null
}

interface LayerRowsRequest {
  startRow: number
  endRow: number
  sortModel: Array<{
    colId: string
    sort: 'asc' | 'desc'
  }>
  filterModel: Record<string, unknown>
}

interface LayerRowsResponse {
  count: number
  data: Record<string, unknown>[]
}

interface FeatureProperties {
  [key: string]: string | number | boolean | null
}

interface GeoJsonFeature {
  type: 'Feature'
  properties: FeatureProperties
  geometry: Record<string, unknown>
}

interface FeatureUpdateRequest {
  featureId: string
  properties: FeatureProperties
}

interface BulkUpdateResult {
  success: boolean
  featureId: string
  error?: string
  result?: unknown
}

interface ApiRequestBody extends Record<string, unknown> {
  layerID: string
  featureID: string | string[]
  attributes?: boolean
  bounds?: number[]
}

// Hook for fetching layer properties
export const useLayerProperties = (layerId: string) => {
  return useQuery<LayerProperty[]>({
    queryKey: ['layer-properties', layerId],
    queryFn: async () => {
      const result = await apiFetch(
        `${apis.apiDatabase.uri}layer/properties/get`,
        'POST',
        {
          layerID: layerId,
          unique: true,
          includeIntrinsicGeom: true,
        }
      )

      // Return the properties array from the nested data property
      return result.data || []
    },
    enabled: !!layerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  })
}

const fetchLayerRowsApi = async (
  layerId: string,
  request: LayerRowsRequest
): Promise<LayerRowsResponse> => {
  const url = `${apis.apiDatabase.uri}layer/export?limit=${request.endRow - request.startRow}&start=${request.startRow}`
  const result: any = await apiFetch(url, 'POST', {
    layerID: layerId,
    exportAs: 'AgGridData',
    filter: request.filterModel,
    sort: request.sortModel,
  })
  return { count: result?.data?.count ?? 0, data: result?.data?.data ?? [] }
}

const hashRequest = (r: LayerRowsRequest) => JSON.stringify(r)

export const useLayerRowsQuery = () => {
  const queryClient = useQueryClient()
  const fetchRows = (layerId: string, request: LayerRowsRequest) => {
    return queryClient.fetchQuery({
      queryKey: ['layer-rows', layerId, hashRequest(request)],
      queryFn: () => fetchLayerRowsApi(layerId, request),
      staleTime: 0,
      gcTime: 5 * 60 * 1000,
    })
  }
  const invalidateLayerRows = (layerId: string) =>
    queryClient.invalidateQueries({ queryKey: ['layer-rows', layerId] })
  return { fetchRows, invalidateLayerRows }
}

export const useLayerRowsFetching = (layerId?: string) =>
  useIsFetching({
    queryKey: layerId ? ['layer-rows', layerId] : ['layer-rows'],
  }) > 0

// Hook for fetching table rows (server-side)
export const useLayerRows = () => {
  return useMutation<
    LayerRowsResponse,
    Error,
    {
      layerId: string
      request: LayerRowsRequest
    }
  >({
    mutationFn: async ({ layerId, request }) => {
      const url = `${apis.apiDatabase.uri}layer/export?limit=${request.endRow - request.startRow}&start=${request.startRow}`

      const result = await apiFetch(url, 'POST', {
        layerID: layerId,
        exportAs: 'AgGridData',
        filter: request.filterModel,
        sort: request.sortModel,
      })

      // Return the data in the expected format
      return {
        count: result.data?.count || 0,
        data: result.data?.data || [],
      }
    },
    retry: 2,
  })
}

// Hook for updating features
//
// `geometry` is optional. When omitted (the default for attribute-only callers
// like AttributeTab and useDocumentLinkUpdater), the body sends `geometry: {}`
// which the backend treats as "don't change geometry." When provided, the
// geometry replaces the stored shape — this is the path used by the
// edit-existing-feature flow (see useUpdateLayerFeatureGeometry).
export const useUpdateFeature = () => {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    Error,
    {
      layerId: string
      featureId: string
      properties: FeatureProperties
      geometry?: Record<string, unknown>
    }
  >({
    mutationFn: async ({ layerId, featureId, properties, geometry }) => {
      const json: GeoJsonFeature = {
        type: 'Feature',
        properties,
        geometry: geometry ?? {},
      }

      const result = await apiFetch(
        `${apis.apiDatabase.uri}layer/geojson/update`,
        'POST',
        {
          layerID: layerId,
          featureID: featureId,
          json,
        }
      )

      return result
    },
    onSuccess: () => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['layer-rows'] })
    },
    retry: 1,
  })
}

// Hook for bulk updating multiple features
export const useBulkUpdateFeatures = () => {
  const queryClient = useQueryClient()

  return useMutation<
    BulkUpdateResult[],
    Error,
    {
      layerId: string
      updates: FeatureUpdateRequest[]
    }
  >({
    mutationFn: async ({ layerId, updates }) => {
      // Process updates in parallel using Promise.allSettled for better error handling
      const promises = updates.map(async ({ featureId, properties }) => {
        const json: GeoJsonFeature = {
          type: 'Feature',
          properties,
          geometry: {},
        }

        try {
          const result = await apiFetch(
            `${apis.apiDatabase.uri}layer/geojson/update`,
            'POST',
            {
              layerID: layerId,
              featureID: featureId,
              json,
            }
          )

          return {
            success: true,
            featureId,
            result,
          } as BulkUpdateResult
        } catch (error) {
          return {
            success: false,
            featureId,
            error: error instanceof Error ? error.message : 'Unknown error',
          } as BulkUpdateResult
        }
      })

      const results = await Promise.allSettled(promises)

      // Process the settled results and extract the actual values or errors
      return results.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value
        }

        return {
          success: false,
          featureId: updates[index].featureId,
          error:
            result.reason instanceof Error
              ? result.reason.message
              : 'Promise rejected',
        } as BulkUpdateResult
      })
    },
    onSuccess: () => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['layer-rows'] })
    },
    retry: 1,
  })
}

// Hook for fetching feature GeoJSON
export const useFeatureGeoJson = () => {
  return useMutation<
    Array<{ data: { features: GeoJsonFeature[] } }>,
    Error,
    {
      layerId: string
      featureIds: string[]
      bounds?: number[]
    }
  >({
    mutationFn: async ({ layerId, featureIds, bounds }) => {
      const fetchList: Array<{ data: { features: GeoJsonFeature[] } }> = []
      const splitCount = 250

      const promises: Promise<{ data: { features: GeoJsonFeature[] } }>[] = []
      for (let index = 0; index < featureIds.length; index += splitCount) {
        const partialFeatureList = featureIds.slice(index, index + splitCount)

        const body: ApiRequestBody = {
          layerID: layerId,
          featureID: partialFeatureList.join(','),
          attributes: false,
        }

        if (bounds) {
          body.bounds = bounds
        }

        const promise = apiFetch(
          `${apis.apiDatabase.uri}layer/geojson/get`,
          'POST',
          body
        ).then(result => {
          return {
            data: {
              features: result.data?.features || [],
            },
          }
        })

        promises.push(promise)
      }

      const results = await Promise.all(promises)
      fetchList.push(...results)

      return fetchList
    },
    retry: 1,
  })
}

// Hook for fetching feature bounds for zoom
export const useFeatureBounds = () => {
  return useMutation<
    { features: GeoJsonFeature[] },
    Error,
    {
      layerId: string
      featureId: string
    }
  >({
    mutationFn: async ({ layerId, featureId }) => {
      const result = await apiFetch(
        `${apis.apiDatabase.uri}layer/geojson/get`,
        'POST',
        {
          layerID: layerId,
          featureID: featureId,
        }
      )

      return {
        features: result.data?.features || [],
      }
    },
    retry: 1,
  })
}

export const useLayerRowsRefresher = () => {
  const { invalidateLayerRows } = useLayerRowsQuery()
  return async (layerId: string) => {
    await invalidateLayerRows(layerId)
    // Notify any open DataTable to refresh its server-side datasource
    if (
      typeof window !== 'undefined' &&
      typeof window.dispatchEvent === 'function'
    ) {
      window.dispatchEvent(
        new CustomEvent('datatable:refresh', { detail: { layerId } })
      )
    }
  }
}
